import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Activity,
    Building2,
    CheckCircle2,
    Home,
    Search,
    ShieldCheck,
    Users,
    XCircle,
} from "lucide-react";

import {
    useNavigate,
} from "react-router-dom";

import api from "../../api/axios";

import AdminNavbar from "../../components/layout/AdminNavbar";

const WFH = () => {

    const navigate =
        useNavigate();

    const [
        employees,
        setEmployees,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        teamFilter,
        setTeamFilter,
    ] = useState("all");

    const [teams, setTeams] = useState([
        { _id: "software", name: "Software", value: "software" },
        { _id: "embedded", name: "Embedded", value: "embedded" },
        { _id: "designteam", name: "Design Team", value: "designteam" },
        { _id: "sales team", name: "Sales Team", value: "sales team" },
        { _id: "tele marketing", name: "Tele Marketing", value: "tele marketing" },
        { _id: "digital marketing", name: "Digital Marketing", value: "digital marketing" }
    ]);

    // ================= FETCH =================
    const fetchEmployees =
        useCallback(async () => {

            try {

                const res =
                    await api.get(
                        "/api/admin/wfh",
                    );

                setEmployees(
                    res.data || [],
                );

            } catch (err) {

                console.error(err);

                if (
                    err.response?.status === 401 ||
                    err.response?.status === 403
                ) {
                    navigate(
                        "/admin/login",
                    );
                }

            } finally {

                setLoading(false);
            }
        }, [navigate]);

    useEffect(() => {
        document.title = "WFH | Savrion Technologies";
        fetchEmployees();
        const fetchTeams = async () => {
            try {
                const res = await api.get('/api/teams');
                if (res.data && res.data.length > 0) {
                    const dbTeams = res.data.map(t => ({ _id: t._id, name: t.name, value: t.name }));
                    const defaults = [
                        { _id: "software", name: "Software", value: "software" },
                        { _id: "embedded", name: "Embedded", value: "embedded" },
                        { _id: "designteam", name: "Design Team", value: "designteam" },
                        { _id: "sales team", name: "Sales Team", value: "sales team" },
                        { _id: "tele marketing", name: "Tele Marketing", value: "tele marketing" },
                        { _id: "digital marketing", name: "Digital Marketing", value: "digital marketing" }
                    ];
                    const merged = [...dbTeams];
                    defaults.forEach(def => {
                        if (!merged.some(t => t.name.toLowerCase() === def.name.toLowerCase())) {
                            merged.push(def);
                        }
                    });
                    setTeams(merged);
                }
            } catch (err) {
                console.error("Failed to fetch teams:", err);
            }
        };
        fetchTeams();
    }, [fetchEmployees]);

    // ================= TOGGLE =================
    const handleToggle =
        async (id) => {

            try {

                const res =
                    await api.patch(
                        `/api/admin/employees/${id}/wfh`,
                    );

                if (
                    res.data &&
                    res.data._id
                ) {

                    setEmployees(
                        (prev) =>
                            prev.map(
                                (emp) =>
                                    emp._id ===
                                        id
                                        ? res.data
                                        : emp,
                            ),
                    );

                } else {

                    setEmployees(
                        (prev) =>
                            prev.map(
                                (emp) =>
                                    emp._id ===
                                        id
                                        ? {
                                            ...emp,
                                            isWFH:
                                                !emp.isWFH,
                                        }
                                        : emp,
                            ),
                    );
                }

            } catch (err) {

                console.error(
                    "WFH toggle failed",
                    err,
                );
            }
        };

    // ================= FILTER =================
    const filteredEmployees =
        useMemo(() => {

            return employees.filter(
                (emp) => {

                    const q =
                        search.toLowerCase();

                    const searchMatch =
                        emp.name
                            ?.toLowerCase()
                            .includes(
                                q,
                            ) ||

                        emp.team
                            ?.toLowerCase()
                            .includes(
                                q,
                            ) ||

                        emp.role
                            ?.toLowerCase()
                            .includes(
                                q,
                            );

                    const teamMatch =
                        teamFilter ===
                        "all" ||
                        emp.team ===
                        teamFilter;

                    return (
                        searchMatch &&
                        teamMatch
                    );
                },
            );

        }, [
            employees,
            search,
            teamFilter,
        ]);

    // ================= STATS =================
    const stats =
        useMemo(() => {

            const total =
                employees.length;

            const wfh =
                employees.filter(
                    (e) =>
                        e.isWFH,
                ).length;

            const office =
                employees.filter(
                    (e) =>
                        !e.isWFH,
                ).length;

            return {
                total,
                wfh,
                office,
            };

        }, [employees]);

    // ================= LOADING =================
    if (loading) {

        return (
            <div
                className="
                min-h-screen

                bg-[var(--bg-primary)]
                text-[var(--text-primary)]

                flex
                items-center
                justify-center
            "
            >
                Loading WFH data...
            </div>
        );
    }

    return (
        <div
            className="
            flex flex-col xl:flex-row
            min-h-screen

            bg-[var(--bg-primary)]
            text-[var(--text-primary)]
        "
        >

            <AdminNavbar />

            <div
                className="
                flex-1
                min-w-0

                px-4
                md:px-6

                py-6

                space-y-6
            "
            >

                {/* ================= HERO ================= */}
                <div
                    className="
                    rounded-xl

                    border
                    border-[var(--border-color)]

                    bg-[var(--card-bg)]

                    px-5
                    py-4

                    relative
                    overflow-hidden
                "
                >

                    <div
                        className="
                        absolute
                        right-0
                        top-0

                        w-32
                        h-32

                        rounded-full

                    bg-[var(--accent-primary)]/5

                        blur-3xl
                    "
                    />

                    <div
                        className="
                        relative

                        flex
                        flex-col
                        lg:flex-row

                        lg:items-center
                        lg:justify-between

                        gap-4
                    "
                    >

                        <div>

                            <div
                                className="
                                inline-flex
                                items-center

                                gap-2

                                px-4
                                py-1.5

                                rounded-lg

                                bg-[var(--accent-primary)]/10

                                text-[var(--accent-primary)]

                                text-xs
                                font-medium

                                mb-2
                            "
                            >
                                <Activity size={16} />
                                Remote Workforce Control
                            </div>

                            <h1
                                className="
                                text-2xl
                                font-bold

                                mb-1
                            "
                            >
                                WFH Management
                            </h1>

                            <p
                                className="
                                text-sm

                                text-[var(--text-secondary)]
                            "
                            >
                                Control work from home
                                access and track remote
                                employees.
                            </p>

                        </div>

                        <div
                            className="
                            w-14
                            h-14

                            rounded-xl

                            bg-[var(--accent-primary)]

                            flex
                            items-center
                            justify-center
                        "
                        >

                            <Home
                                size={26}
                                className="text-white"
                            />

                        </div>

                    </div>

                </div>

                {/* ================= STATS ================= */}
                <div
                    className="
                    grid
                    grid-cols-1
                    sm:grid-cols-2
                    xl:grid-cols-3

                    gap-5
                "
                >

                    {[
                        {
                            title:
                                "Total Employees",
                            value:
                                stats.total,
                            icon:
                                <Users size={24} />,
                        },

                        {
                            title:
                                "WFH Enabled",
                            value:
                                stats.wfh,
                            icon:
                                <CheckCircle2 size={24} />,
                        },

                        {
                            title:
                                "Office Mode",
                            value:
                                stats.office,
                            icon:
                                <Building2 size={24} />,
                        },
                    ].map(
                        (
                            item,
                            index,
                        ) => (
                            <div
                                key={index}
                                className="
                                rounded-xl

                                border
                                border-[var(--border-color)]

                                bg-[var(--card-bg)]

                                p-4
                            "
                            >

                                <div
                                    className="
                                    w-9
                                    h-9

                                    rounded-lg

                                    bg-[var(--accent-primary)]/10

                                    text-[var(--accent-primary)]

                                    flex
                                    items-center
                                    justify-center

                                    mb-3
                                "
                                >
                                    {item.icon}
                                </div>

                                <h2
                                    className="
                                    text-2xl
                                    font-bold

                                    mb-0.5
                                "
                                >
                                    {item.value}
                                </h2>

                                <p
                                    className="
                                    text-[var(--text-secondary)]
                                    text-sm
                                "
                                >
                                    {item.title}
                                </p>

                            </div>
                        ),
                    )}

                </div>

                {/* ================= FILTERS ================= */}
                <div
                    className="
                    rounded-xl

                    border
                    border-[var(--border-color)]

                    bg-[var(--card-bg)]

                    p-4

                    flex
                    flex-col
                    xl:flex-row

                    gap-4
                "
                >

                    {/* SEARCH */}
                    <div
                        className="
                        relative

                        flex-1
                    "
                    >

                        <Search
                            size={18}
                            className="
                            absolute
                            left-4
                            top-1/2
                            -translate-y-1/2

                            text-[var(--text-secondary)]
                        "
                        />

                        <input
                            type="text"
                            placeholder="Search employee..."
                            value={search}
                            onChange={(e) =>
                                setSearch(
                                    e.target
                                        .value,
                                )
                            }
                            className="
                            w-full
                            h-12

                            rounded-lg

                            border
                            border-[var(--border-color)]

                            bg-[var(--bg-primary)]

                            pl-11
                            pr-4

                            outline-none

                            focus:border-[var(--accent-primary)]
                        "
                        />

                    </div>

                    {/* TEAM */}
                    <select
                        value={
                            teamFilter
                        }
                        onChange={(e) =>
                            setTeamFilter(
                                e.target
                                    .value,
                            )
                        }
                        className="
                        h-12

                        rounded-lg

                        border
                        border-[var(--border-color)]

                        bg-[var(--bg-primary)]

                        px-4

                        outline-none
                    "
                    >
                        <option value="all">
                            All Teams
                        </option>

                        {teams.map(t => (
                            <option key={t._id} value={t.value}>
                                {t.name}
                            </option>
                        ))}
                    </select>

                </div>

                {/* ================= TABLE ================= */}
                <div
                    className="
                    overflow-hidden

                    rounded-xl

                    border
                    border-[var(--border-color)]

                    bg-[var(--card-bg)]
                "
                >

                    <div className="overflow-x-auto">
                    <table
                        className="
                        min-w-[920px]
                        w-full
                        border-collapse
                        text-sm
                    "
                    >

                        <thead
                            className="
                            bg-[var(--bg-primary)]
                        "
                        >

                            <tr>

                                {[
                                    "Employee",
                                    "Team",
                                    "Role",
                                    "WFH Status",
                                    "Actions",
                                ].map(
                                    (
                                        head,
                                    ) => (
                                        <th
                                            key={
                                                head
                                            }
                                            className="
                                            text-left

                                            px-5
                                            py-4

                                            border
                                            border-[var(--border-color)]

                                            text-xs
                                            font-bold
                                            uppercase
                                            tracking-wide
                                            text-[var(--text-secondary)]
                                        "
                                        >
                                            {head}
                                        </th>
                                    ),
                                )}

                            </tr>

                        </thead>

                        <tbody>

                            {filteredEmployees.map(
                                (
                                    emp,
                                ) => (

                                    <tr
                                        key={
                                            emp._id
                                        }
                                        className="
                                        hover:bg-[var(--bg-primary)]

                                        transition-all
                                    "
                                    >

                                        {/* EMPLOYEE */}
                                        <td
                                            className="
                                            px-5
                                            py-4

                                            border
                                            border-[var(--border-color)]
                                        "
                                        >

                                            <div
                                                className="
                                                flex
                                                items-center

                                                gap-4
                                            "
                                            >

                                                <div
                                                    className="
                                                    w-12
                                                    h-12

                                                    rounded-xl

                                                    bg-[var(--accent-primary)]/10

                                                    text-[var(--accent-primary)]

                                                    flex
                                                    items-center
                                                    justify-center

                                                    font-bold
                                                "
                                                >
                                                    {emp.name
                                                        ?.charAt(
                                                            0,
                                                        )
                                                        ?.toUpperCase()}
                                                </div>

                                                <div>

                                                    <h3
                                                        className="
                                                        font-bold
                                                        text-[var(--text-primary)]
                                                    "
                                                    >
                                                        {
                                                            emp.name
                                                        }
                                                    </h3>

                                                    <p
                                                        className="
                                                        text-sm

                                                        text-[var(--text-secondary)]
                                                        break-all
                                                    "
                                                    >
                                                        {
                                                            emp.email
                                                        }
                                                    </p>

                                                </div>

                                            </div>

                                        </td>

                                        {/* TEAM */}
                                        <td
                                            className="
                                            px-5
                                            py-4

                                            border
                                            border-[var(--border-color)]

                                            capitalize
                                            text-[var(--text-primary)]
                                        "
                                        >
                                            {
                                                emp.team
                                            }
                                        </td>

                                        {/* ROLE */}
                                        <td
                                            className="
                                            px-5
                                            py-4

                                            border
                                            border-[var(--border-color)]

                                            capitalize
                                            text-[var(--text-primary)]
                                        "
                                        >
                                            {
                                                emp.role
                                            }
                                        </td>

                                        {/* STATUS */}
                                        <td
                                            className="
                                            px-5
                                            py-4

                                            border
                                            border-[var(--border-color)]
                                        "
                                        >

                                            <span
                                                className={`
                                                inline-flex
                                                items-center

                                                px-3
                                                py-1.5

                                                rounded-md

                                                text-xs
                                                font-bold
                                                whitespace-nowrap

                                                ${emp.isWFH
                                                        ? "bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/20"
                                                        : "bg-neutral-500/10 text-[var(--text-secondary)] ring-1 ring-neutral-500/20"
                                                    }
                                            `}
                                            >
                                                {emp.isWFH
                                                    ? "WFH Enabled"
                                                    : "Office Mode"}
                                            </span>

                                        </td>

                                        {/* ACTION */}
                                        <td
                                            className="
                                            px-5
                                            py-4

                                            border
                                            border-[var(--border-color)]
                                        "
                                        >

                                            <button
                                                onClick={() =>
                                                    handleToggle(
                                                        emp._id,
                                                    )
                                                }
                                                className={`
                                                h-11

                                                px-5

                                                rounded-lg

                                                text-sm
                                                font-bold

                                                transition-all

                                                inline-flex
                                                items-center
                                                justify-center

                                                gap-2
                                                whitespace-nowrap

                                                ${emp.isWFH
                                                        ? "bg-red-500/10 text-red-500 hover:bg-red-500/20"
                                                        : "bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-hover)]"
                                                    }
                                            `}
                                            >

                                                {emp.isWFH ? (
                                                    <>
                                                        <XCircle size={16} />
                                                        Disable WFH
                                                    </>
                                                ) : (
                                                    <>
                                                        <ShieldCheck size={16} />
                                                        Enable WFH
                                                    </>
                                                )}

                                            </button>

                                        </td>

                                    </tr>
                                ),
                            )}

                        </tbody>

                    </table>
                    </div>

                    {filteredEmployees.length ===
                        0 && (
                            <div
                                className="
                                py-20

                                text-center

                                text-[var(--text-secondary)]
                            "
                            >
                                No employees found
                            </div>
                        )}

                </div>

            </div>

        </div>
    );
};

export default WFH;


